import { useState, useRef, useEffect } from "react"
import { X, ChevronUp, ChevronDown, CheckCircle2, Dumbbell } from "lucide-react"
import { motion, AnimatePresence } from "framer-motion"
import type { Routine, RoutineExercise, WorkoutSetData } from "../../types"
import { createWorkoutLog } from "../../api/workoutLogs"

interface Props {
  routine: Routine
  today: string
  onComplete: () => void
  onClose: () => void
}

const MUSCLE_COLORS: Record<string, { pill: string; dot: string }> = {
  legs:      { pill: "bg-green-500/15 text-green-400 border-green-500/30",    dot: "bg-green-400" },
  chest:     { pill: "bg-red-500/15 text-red-400 border-red-500/30",          dot: "bg-red-400" },
  back:      { pill: "bg-blue-500/15 text-blue-400 border-blue-500/30",       dot: "bg-blue-400" },
  shoulders: { pill: "bg-purple-500/15 text-purple-400 border-purple-500/30", dot: "bg-purple-400" },
  arms:      { pill: "bg-orange-500/15 text-orange-400 border-orange-500/30", dot: "bg-orange-400" },
  core:      { pill: "bg-yellow-500/15 text-yellow-400 border-yellow-500/30", dot: "bg-yellow-400" },
}
function getMuscle(group: string) {
  return MUSCLE_COLORS[group?.toLowerCase()] ?? { pill: "bg-white/10 text-white/60 border-white/20", dot: "bg-white/40" }
}

function emptySet(): WorkoutSetData {
  return { reps: "", weight: "", rest: "" }
}
function initSets(re: RoutineExercise): WorkoutSetData[] {
  return Array.from({ length: re.sets }, () => emptySet())
}

function repsLabel(unit: string): string {
  const map: Record<string, string> = {
    reps: "reps",
    reps_per_side: "c/lado",
    rir: "RIR",
    rpe: "RPE",
  }
  return map[unit] ?? unit
}

export default function WorkoutSession({ routine, today, onComplete, onClose }: Props) {
  const exercises = routine.routine_exercises
  const [activeIdx, setActiveIdx] = useState(0)
  const [setsData, setSetsData] = useState<Record<number, WorkoutSetData[]>>(() => {
    const init: Record<number, WorkoutSetData[]> = {}
    exercises.forEach((re) => { init[re.id] = initSets(re) })
    return init
  })
  const [completedExercises, setCompletedExercises] = useState<Set<number>>(() => new Set())
  const [saving, setSaving] = useState(false)
  const [duration, setDuration] = useState("")

  const tabsRef = useRef<HTMLDivElement>(null)
  const activeExercise = exercises[activeIdx]

  // Scroll active tab into view on mobile
  useEffect(() => {
    const container = tabsRef.current
    if (!container) return
    const active = container.children[activeIdx] as HTMLElement | undefined
    active?.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "center" })
  }, [activeIdx])

  function updateSet(reId: number, setIdx: number, field: keyof WorkoutSetData, value: string) {
    setSetsData((prev) => {
      const sets = [...(prev[reId] ?? [])]
      sets[setIdx] = { ...sets[setIdx], [field]: value }
      return { ...prev, [reId]: sets }
    })
  }

  function isDone(re: RoutineExercise) {
    return completedExercises.has(re.id) || (setsData[re.id] ?? []).some((s) => s.reps || s.weight)
  }

  const completedCount = exercises.filter(isDone).length
  const allDone = completedCount === exercises.length

  function handleMarkExercise() {
    const newCompleted = new Set(completedExercises)
    newCompleted.add(activeExercise.id)
    setCompletedExercises(newCompleted)
    if (activeIdx < exercises.length - 1) {
      setActiveIdx((i) => i + 1)
    } else {
      handleComplete()
    }
  }

  async function handleComplete() {
    setSaving(true)
    try {
      const workoutExercises = exercises.map((re) => {
        const sets = setsData[re.id] ?? []
        const filled = sets.filter((s) => s.reps || s.weight)
        return {
          exercise_id: re.exercise.id,
          sets_completed: filled.length || re.sets,
          reps_completed: filled[0] ? Number(filled[0].reps) || re.reps : re.reps,
          weight_used:    filled[0] ? Number(filled[0].weight) || 0 : 0,
          sets_data: JSON.stringify(sets),
        }
      })
      await createWorkoutLog({
        routine_id: routine.id,
        performed_at: today,
        duration_minutes: duration ? Number(duration) : undefined,
        workout_exercises_attributes: workoutExercises,
      })
      onComplete()
    } finally {
      setSaving(false)
    }
  }

  return (
    <motion.div
      className="fixed inset-0 z-50 flex flex-col bg-[#0d0d0d]"
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 24 }}
      transition={{ duration: 0.2 }}
    >
      {/* ── Header ── */}
      <header className="flex-shrink-0 flex items-center justify-between gap-3 border-b border-white/10 bg-surface px-4 py-3 md:px-6">
        <div className="min-w-0">
          <h2 className="truncate font-bold text-white tracking-wide uppercase text-sm md:text-base">
            {routine.name}
          </h2>
          <p className="text-xs text-muted">
            {completedCount}/{exercises.length} ejercicios
            <span className="ml-2 inline-block h-1.5 w-16 rounded-full bg-white/10 align-middle overflow-hidden">
              <span
                className="block h-full bg-primary rounded-full transition-all duration-500"
                style={{ width: `${(completedCount / exercises.length) * 100}%` }}
              />
            </span>
          </p>
        </div>
        <button
          onClick={onClose}
          className="flex-shrink-0 rounded-full p-2 text-muted hover:text-white hover:bg-white/10 transition"
        >
          <X size={18} />
        </button>
      </header>

      {/* ── Mobile: horizontal exercise tabs ── */}
      <div
        ref={tabsRef}
        className="md:hidden flex-shrink-0 flex gap-2 overflow-x-auto border-b border-white/10 bg-surface px-3 py-2 scrollbar-none"
        style={{ scrollbarWidth: "none" }}
      >
        {exercises.map((re, idx) => {
          const done = isDone(re)
          const active = activeIdx === idx
          const mc = getMuscle(re.exercise.muscle_group)
          return (
            <button
              key={re.id}
              onClick={() => setActiveIdx(idx)}
              className={`flex-shrink-0 flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium transition border ${
                active
                  ? "bg-primary/20 border-primary text-primary"
                  : "border-white/10 text-muted hover:border-white/30 hover:text-white"
              }`}
            >
              {done
                ? <CheckCircle2 size={11} className="text-success" />
                : <span className={`w-2 h-2 rounded-full ${mc.dot}`} />
              }
              {re.exercise.name}
            </button>
          )
        })}
      </div>

      {/* ── Body: sidebar (desktop) + main ── */}
      <div className="flex flex-1 overflow-hidden">

        {/* Desktop sidebar */}
        <aside className="hidden md:flex flex-col w-52 lg:w-60 flex-shrink-0 border-r border-white/10 bg-surface overflow-y-auto">
          {exercises.map((re, idx) => {
            const done = isDone(re)
            const active = activeIdx === idx
            const mc = getMuscle(re.exercise.muscle_group)
            return (
              <button
                key={re.id}
                onClick={() => setActiveIdx(idx)}
                className={`w-full text-left px-4 py-3.5 border-b border-white/5 transition-colors ${
                  active
                    ? "bg-primary/10 border-l-[3px] border-l-primary"
                    : "hover:bg-white/5 border-l-[3px] border-l-transparent"
                }`}
              >
                <div className="flex items-start gap-2">
                  <div className="mt-0.5 flex-shrink-0">
                    {done
                      ? <CheckCircle2 size={13} className="text-success" />
                      : <span className={`block w-2.5 h-2.5 rounded-full mt-0.5 ${mc.dot} opacity-70`} />
                    }
                  </div>
                  <div className="min-w-0">
                    <p className={`text-sm font-medium leading-tight line-clamp-2 ${active ? "text-white" : "text-white/80"}`}>
                      {re.exercise.name}
                    </p>
                    <span className={`inline-block mt-1 text-[10px] rounded-full px-1.5 py-0.5 border ${mc.pill}`}>
                      {re.exercise.muscle_group}
                    </span>
                  </div>
                </div>
              </button>
            )
          })}
        </aside>

        {/* Main content */}
        <div className="flex-1 overflow-y-auto">
          <AnimatePresence mode="wait">
            <motion.div
              key={activeExercise.id}
              className="p-4 sm:p-5 md:p-6 space-y-5 max-w-2xl mx-auto"
              initial={{ opacity: 0, x: 8 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -8 }}
              transition={{ duration: 0.14 }}
            >
              {/* Exercise title + nav */}
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-xs text-muted mb-1">
                    Ejercicio {activeIdx + 1} de {exercises.length}
                  </p>
                  <h3 className="text-xl sm:text-2xl font-bold text-white leading-tight">
                    {activeExercise.exercise.name}
                  </h3>
                  <span className={`inline-block mt-1.5 text-xs rounded-full px-2.5 py-0.5 border ${getMuscle(activeExercise.exercise.muscle_group).pill}`}>
                    {activeExercise.exercise.muscle_group}
                  </span>
                </div>
                {/* Desktop nav arrows */}
                <div className="hidden md:flex flex-col gap-1 flex-shrink-0">
                  <button
                    onClick={() => setActiveIdx((i) => Math.max(0, i - 1))}
                    disabled={activeIdx === 0}
                    className="rounded-full p-1.5 border border-white/10 text-muted hover:text-white hover:border-white/30 disabled:opacity-25 transition"
                  >
                    <ChevronUp size={15} />
                  </button>
                  <button
                    onClick={() => setActiveIdx((i) => Math.min(exercises.length - 1, i + 1))}
                    disabled={activeIdx === exercises.length - 1}
                    className="rounded-full p-1.5 border border-white/10 text-muted hover:text-white hover:border-white/30 disabled:opacity-25 transition"
                  >
                    <ChevronDown size={15} />
                  </button>
                </div>
              </div>

              {/* GIF */}
              <div className="rounded-2xl border border-white/10 bg-surface overflow-hidden w-full flex items-center justify-center">
                {activeExercise.exercise.gif_url ? (
                  <img
                    src={activeExercise.exercise.gif_url}
                    alt={activeExercise.exercise.name}
                    className="w-auto max-w-full max-h-72 sm:max-h-80 md:max-h-96 object-contain"
                  />
                ) : (
                  <div className="h-40 flex flex-col items-center justify-center gap-2 text-muted">
                    <Dumbbell size={28} className="opacity-30" />
                    <p className="text-xs opacity-50">{activeExercise.exercise.name}</p>
                  </div>
                )}
              </div>

              {/* Planned spec */}
              <div className="flex flex-wrap gap-2">
                <span className="rounded-full bg-white/5 border border-white/10 px-3 py-1 text-xs text-white/70">
                  {activeExercise.sets} series
                </span>
                <span className="rounded-full bg-white/5 border border-white/10 px-3 py-1 text-xs text-white/70">
                  {activeExercise.reps} {repsLabel(activeExercise.reps_unit)}
                </span>
                {activeExercise.weight ? (
                  <span className="rounded-full bg-white/5 border border-white/10 px-3 py-1 text-xs text-white/70">
                    {activeExercise.weight} {activeExercise.weight_unit}
                  </span>
                ) : null}
                {activeExercise.notes ? (
                  <span className="rounded-full bg-white/5 border border-white/10 px-3 py-1 text-xs text-white/50 italic">
                    {activeExercise.notes}
                  </span>
                ) : null}
              </div>

              {/* Sets table */}
              <div className="space-y-3">
                <p className="text-[11px] font-semibold text-muted uppercase tracking-widest">
                  Series realizadas
                </p>

                {/* Header row */}
                <div className="grid gap-2" style={{ gridTemplateColumns: "72px 1fr 1fr 1fr" }}>
                  {["", "Reps", "Peso", "Rec"].map((h) => (
                    <p key={h} className="text-center text-[10px] text-muted font-medium uppercase tracking-wide">{h}</p>
                  ))}
                </div>

                {(setsData[activeExercise.id] ?? []).map((set, setIdx) => (
                  <div
                    key={setIdx}
                    className="grid gap-2 items-center"
                    style={{ gridTemplateColumns: "72px 1fr 1fr 1fr" }}
                  >
                    {/* Serie label */}
                    <span className="rounded-full border border-white/20 bg-white/5 px-2 py-2 text-xs font-semibold text-white/80 text-center">
                      Serie {setIdx + 1}
                    </span>

                    {/* Reps */}
                    <div className="flex items-center rounded-full border border-white/20 bg-surface overflow-hidden focus-within:border-primary transition-colors">
                      <input
                        type="number"
                        inputMode="numeric"
                        value={set.reps}
                        onChange={(e) => updateSet(activeExercise.id, setIdx, "reps", e.target.value)}
                        placeholder={String(activeExercise.reps)}
                        className="w-full bg-transparent py-2 text-sm text-white outline-none text-center placeholder:text-white/25"
                      />
                    </div>

                    {/* Peso */}
                    <div className="flex items-center rounded-full border border-white/20 bg-surface overflow-hidden focus-within:border-primary transition-colors">
                      <input
                        type="number"
                        inputMode="decimal"
                        value={set.weight}
                        onChange={(e) => updateSet(activeExercise.id, setIdx, "weight", e.target.value)}
                        placeholder={activeExercise.weight ? String(activeExercise.weight) : "—"}
                        className="w-full bg-transparent py-2 text-sm text-white outline-none text-center placeholder:text-white/25"
                      />
                    </div>

                    {/* Rec */}
                    <div className="flex items-center rounded-full border border-white/20 bg-surface overflow-hidden focus-within:border-primary transition-colors">
                      <input
                        type="number"
                        inputMode="numeric"
                        value={set.rest}
                        onChange={(e) => updateSet(activeExercise.id, setIdx, "rest", e.target.value)}
                        placeholder="90s"
                        className="w-full bg-transparent py-2 text-sm text-white outline-none text-center placeholder:text-white/25"
                      />
                    </div>
                  </div>
                ))}
              </div>

              {/* Bottom padding so footer doesn't cover content on mobile */}
              <div className="h-2 md:h-0" />
            </motion.div>
          </AnimatePresence>
        </div>
      </div>

      {/* ── Footer ── */}
      <footer className="flex-shrink-0 border-t border-white/10 bg-surface px-4 py-3 md:px-6">
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 max-w-2xl mx-auto">
          {/* Duration */}
          <div className="flex items-center rounded-full border border-white/20 bg-surface-light overflow-hidden self-center sm:self-auto">
            <span className="px-3 py-2.5 text-xs text-muted border-r border-white/10 whitespace-nowrap">Duración</span>
            <input
              type="number"
              inputMode="numeric"
              value={duration}
              onChange={(e) => setDuration(e.target.value)}
              placeholder="60"
              className="w-14 bg-transparent px-2 py-2.5 text-sm text-white outline-none text-center"
            />
            <span className="px-2.5 py-2.5 text-xs text-muted border-l border-white/10">min</span>
          </div>

          {/* Complete button */}
          <button
            onClick={handleMarkExercise}
            disabled={saving}
            className="flex-1 rounded-full py-3 text-sm font-semibold transition disabled:opacity-60 bg-primary text-black hover:bg-primary-hover active:scale-[0.98]"
          >
            {saving ? "Guardando..." : allDone ? "Completar entrenamiento" : "Marcar como completado"}
          </button>
        </div>
      </footer>
    </motion.div>
  )
}
